import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TabsBar } from '@/common/TabsBar';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/follow-ups',
)({
  component: MemberFollowUpsRoute,
});

export default function MemberFollowUpsRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const studentId = Number(memberId);

  // Legacy refuses this tab by never registering it: `setup_nav()` returns
  // before adding a single subnav when `hasAccess()` is false
  // (`themes/wbs/modules/follow-up/class-student-follow-up.php:48-71`), so a
  // denied viewer gets an HTTP 404 page rather than a permission message.
  // The probe answers 200 for every authenticated caller — it is a capability
  // question, not an existence oracle over students — so one query decides the
  // page without reading a status code, and it carries the caption the three
  // tables need without widening `getStudent` (17 fields, admin-or-self).
  const { data: access, isLoading } = api.getStudentFollowUpsAccess.useQuery({
    path: { student_id: studentId },
  });

  // Not a no-op: rendering the tab strip before `access` resolves flashes it at
  // a viewer the next line refuses.
  if (isLoading) return null;

  // Positive test. `access` is `undefined` on a 401, a 500 or a dropped
  // connection, and every one of those is a refusal — a `can_view === false`
  // test falls through on all three and renders the strip plus <Outlet/>.
  if (access?.can_view !== true) return <NotFoundScreen />;

  // EXACTLY three, in registration order:
  // `class-student-follow-up.php:29-42` registers `follow-up-requests`,
  // `pending-requests` and `entries` and nothing else.
  const tabs = [
    { title: t('wbs.follow-up-requests'), to: '' },
    { title: t('wbs.pending-requests'), to: 'pending-requests' },
    { title: t('sidebar.follow-ups'), to: 'entries' },
  ];

  return (
    <>
      <TabsBar
        tabs={tabs}
        base={'/members/$memberId/follow-ups'}
        tabLevel={4}
      />
      <MainContent>
        <Outlet />
      </MainContent>
    </>
  );
}

const MainContent = styled.main`
  background: var(--color-background);
  flex: 1;
  min-width: 0;
`;
